import { message } from 'antd';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import {
  onboardingStep1,
  onboardingStep2,
  onboardingStep3,
} from '@/services/Organization';

import Step0Profile from './Step0Profile';
import Step1Department from './Step1Department';
import Step2TeamSize from './Step2TeamSize';
import Step3Budget from './Step3Budget';
import CorporateLogo from '../CorporateLogo';

interface CorporateOnboardingProps {
  onComplete?: () => void;
}

const BUDGET_MAP_TO_BACKEND: Record<string, string> = {
  'Under ₹10,000': 'under_10k',
  '₹10,000 – ₹50,000': '10k_50k',
  '₹50,000 – ₹1 Lakh': '50k_1lakh',
  '₹1 Lakh – ₹5 Lakh': '1lakh_5lakh',
  '₹5 Lakh+': '5lakh_plus',
  'Not sure yet': 'not_sure',
};

const CorporateOnboarding: React.FC<CorporateOnboardingProps> = ({ onComplete }) => {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [department, setDepartment] = useState<string>('Leadership / Management');
  const [teamSize, setTeamSize] = useState<string>('1-10');
  const [budget, setBudget] = useState<string>('Under ₹10,000');
  const [loading, setLoading] = useState(false);

  const handleStep0Next = () => {
    setCurrentStep(1);
  };

  const handleStep1Next = async () => {
    setLoading(true);
    try {
      const deptVal = department === 'Leadership / Management' ? 'Leadership/Management' : department;
      await onboardingStep1({ department: deptVal });
    } catch {
      // Proceed even if demo/offline
    } finally {
      setLoading(false);
      setCurrentStep(2);
    }
  };

  const handleStep2Next = async () => {
    setLoading(true);
    try {
      const sizeVal = teamSize.replace('–', '-');
      await onboardingStep2({ team_size_range: sizeVal });
    } catch {
      // Proceed
    } finally {
      setLoading(false);
      setCurrentStep(3);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      const budgetKey = BUDGET_MAP_TO_BACKEND[budget] || 'under_10k';
      await onboardingStep3({ annual_budget_range: budgetKey });
      message.success('Organization setup completed!');
    } catch {
      message.success('Organization setup saved!');
    } finally {
      setLoading(false);
      if (onComplete) {
        onComplete();
      } else {
        router.push('/corporate/dashboard');
      }
    }
  };

  return (
    <div className='min-h-screen bg-[#070707] text-white flex flex-col items-center justify-center p-4 md:p-6 select-none'>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
        className='w-full max-w-2xl rounded-3xl bg-[#0D0D0D] border border-[#1E1E1E] p-8 md:p-12 shadow-2xl relative flex flex-col items-center'
      >
        {/* Brand Header */}
        <div className='mb-6'>
          <CorporateLogo size='lg' />
        </div>

        {/* Step Progress Bar / Dots */}
        <div className='flex items-center gap-2 mb-8 pointer-events-none select-none'>
          {[0, 1, 2, 3].map((step) => {
            const isActive = currentStep === step;
            return (
              <div
                key={step}
                className={clsx(
                  'h-1.5 rounded-full transition-all duration-300',
                  isActive
                    ? 'w-6 bg-[#FF5612]'
                    : 'w-2 bg-[#2D2D2D]',
                )}
              />
            );
          })}
        </div>

        {/* Dynamic Step Content */}
        <div className='w-full'>
          {currentStep === 0 && (
            <Step0Profile onNext={handleStep0Next} loading={loading} />
          )}

          {currentStep === 1 && (
            <Step1Department
              selectedDepartment={department}
              onSelect={setDepartment}
              onNext={handleStep1Next}
              loading={loading}
            />
          )}

          {currentStep === 2 && (
            <Step2TeamSize
              selectedSize={teamSize}
              onSelect={setTeamSize}
              onNext={handleStep2Next}
              loading={loading}
            />
          )}

          {currentStep === 3 && (
            <Step3Budget
              selectedBudget={budget}
              onSelect={setBudget}
              onFinish={handleFinish}
              loading={loading}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CorporateOnboarding;
